import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import type { RootState } from '../index';
import {
  packAnalysisService,
  type CartonizationRequest,
} from '../../services/api/packAnalysisService';
import type { Item } from '../../services/api/itemMasterService';
import {
  setIngestProgress,
  setIngestCurrentBatchId,
  applyIngestProgressEvent,
  setActiveBatchStarted,
  setActiveBatchCleared,
  addPrepackGroup,
  addPackPlannerPrepackGroup,
} from '../slices/packAnalysisWorkbenchSlice';
import { toResolvedItemRef } from '../../features/pack-analysis-workbench/components/Simulator/itemResolution';
import type { ResolvedItemRef } from '../../features/pack-analysis-workbench/components/Simulator/lineModel';
import { subscribeToIngestProgress } from '../../services/api/ingestProgressStream';
import { logger } from '../../utils/logger';

/**
 * Module-private map of in-flight ingest uploads keyed by clientBatchId.
 * Lets {@link cancelIngestBatch} look up the right AbortController to abort
 * the multipart upload from outside the {@link ingestBatch} thunk's scope.
 * Entries are removed in the ingest thunk's `finally` so a stale entry can't
 * pin a controller indefinitely. Module-level on purpose — Redux state must
 * stay serializable, and the controller is local UI plumbing not domain data.
 */
const inFlightUploadAborts = new Map<string, AbortController>();

// Sentinel value distinguishing "user clicked cancel" from a real error.
// Lets the slice/UI render a quiet "cancelled" state instead of a red alert.
export const INGEST_CANCELLED_SENTINEL = '__ingest_cancelled_by_user__';

// ── Workbench: Ingest batch (multipart upload + SSE per-file progress) ────

export const ingestBatch = createAsyncThunk(
  'packAnalysisWorkbench/ingestBatch',
  async (
    args: {
      tenantId: number;
      files: File[];
      dataSource?: string;
      direction?: string;
      documentVariant?: string;
      buyerCodeHint?: string;
      dryRun?: boolean;
    },
    { rejectWithValue, dispatch }
  ) => {
    // Generate batch correlation id BEFORE the POST so the SSE subscription and
    // the multipart submission share the same key. crypto.randomUUID is in all
    // evergreen browsers (Chrome 92+, Firefox 95+, Safari 15.4+); fall back to
    // a random string for older clients.
    const clientBatchId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    // Surface the id to slice state so the Cancel button can target this batch
    // without props-drilling. Cleared on the thunk's fulfilled/rejected.
    dispatch(setIngestCurrentBatchId(clientBatchId));

    // Spec pack-analysis-ingest-background-progress Req 5.2 — stamp the
    // global activeBatch summary on entry. totalFiles starts at 0 and is
    // patched by the first batch_started SSE event (the file count the
    // server sees after multipart parse can differ from args.files.length
    // by zero today, but the SSE is authoritative). Single-active-batch
    // v1 invariant lives inside the reducer.
    dispatch(
      setActiveBatchStarted({
        clientBatchId,
        totalFiles: args.files.length,
        startedAt: new Date().toISOString(),
      })
    );

    // Open the SSE stream first. The handler publishes events as soon as files
    // start; if we connected after the POST, we'd race the first file_started.
    // (The publisher does buffer events for late connectors, but minimizing
    // buffer churn is cheap and clearer.)
    const sseAbort = new AbortController();
    const ssePromise = subscribeToIngestProgress(clientBatchId, {
      signal: sseAbort.signal,
      onEvent: (evt) => {
        dispatch(applyIngestProgressEvent(evt));
      },
      onError: (err) => {
        logger.warn('SSE: ingest progress stream error', err);
      },
    }).catch((err) => {
      // Don't abort the whole batch on SSE failure — UI just won't have live
      // per-file rows. The http response will still fill in the result table.
      logger.warn('SSE: ingest progress subscribe failed', err);
    });

    // Per-batch upload controller: lets {@link cancelIngestBatch} abort the
    // multipart upload while it's still streaming bytes (before the server
    // even starts the batch handler). Stored in the module map so the cancel
    // thunk can look it up by clientBatchId.
    const uploadAbort = new AbortController();
    inFlightUploadAborts.set(clientBatchId, uploadAbort);

    try {
      const startedAt = Date.now();
      const data = await packAnalysisService.ingestBatch(args.tenantId, args.files, {
        dataSource: args.dataSource,
        direction: args.direction,
        documentVariant: args.documentVariant,
        buyerCodeHint: args.buyerCodeHint,
        dryRun: args.dryRun,
        clientBatchId,
        signal: uploadAbort.signal,
        onUploadProgress: (loaded, total) => {
          dispatch(
            setIngestProgress({
              phase: loaded >= total ? 'processing' : 'uploading',
              bytesLoaded: loaded,
              bytesTotal: total,
            })
          );
        },
      });

      // Drain remaining SSE events. Server publishes batch_completed and closes
      // the channel before returning the http response, so this should be near-
      // instant. Cap with a 1.5 s timeout in case the SSE stream is wedged.
      await Promise.race([
        ssePromise,
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
      sseAbort.abort();

      return { data, totalElapsedMs: Date.now() - startedAt };
    } catch (error: unknown) {
      sseAbort.abort();
      // Distinguish "user cancelled" from a genuine ingest failure — axios
      // throws CanceledError when AbortController.abort() is called while the
      // upload is in-flight. The slice can then render a quiet "cancelled"
      // state instead of a red ingest-failed alert.
      if (axios.isCancel(error) || (error as { name?: string })?.name === 'CanceledError') {
        return rejectWithValue(INGEST_CANCELLED_SENTINEL);
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Ingest failed');
    } finally {
      // Always clear the map entry — leaving a stale controller would let a
      // late cancel POST abort the *next* batch with the same id (very unlikely
      // since the id is a UUID, but the bookkeeping is cheap).
      inFlightUploadAborts.delete(clientBatchId);

      // Spec pack-analysis-ingest-background-progress Req 5.3 — single
      // non-null → null transition that fires the AppShell boot hook's
      // completion-toast + Browser-Notification effect. MUST live in
      // finally so success / cancel / error all converge on the same edge.
      dispatch(setActiveBatchCleared());
    }
  }
);

/**
 * Cancel an in-flight ingest batch. Phase-aware two-prong:
 *   1. While the multipart upload is still streaming bytes (phase='uploading'),
 *      abort the local request so the server never finishes receiving it.
 *   2. After the upload is done (phase='processing'), the same HTTP connection
 *      is staying open to serve the response — aborting it would also kill
 *      the response stream and any per-file SSE updates that haven't drained
 *      yet, leaving the UI stuck mid-batch even though the server completed.
 *      In that phase we send only the server cancel POST and let the original
 *      POST + the SSE stream finish naturally; queued files flip to
 *      "Cancelled" via SSE within milliseconds, in-flight files finish
 *      atomically (GitHub-Actions semantics), and the http response returns
 *      with the full per-file result rows.
 */
export const cancelIngestBatch = createAsyncThunk(
  'packAnalysisWorkbench/cancelIngestBatch',
  async (clientBatchId: string, { rejectWithValue, getState }) => {
    if (!clientBatchId) return rejectWithValue('clientBatchId required');

    const state = getState() as RootState;
    const uploadPhase = state.packAnalysisWorkbench.ingest.progress.phase;

    // 1. Only abort the upload while bytes are still flying. After the upload
    //    completes, the connection is the response channel — we don't want to
    //    kill it.
    if (uploadPhase === 'uploading') {
      const uploadAbort = inFlightUploadAborts.get(clientBatchId);
      if (uploadAbort) {
        try {
          uploadAbort.abort();
        } catch (err) {
          logger.warn('Cancel: upload abort threw (ignored)', err);
        }
      }
    }

    // 2. Tell the server to stop queued files. Idempotent on the server —
    //    found=false just means the batch wasn't running there.
    try {
      return await packAnalysisService.cancelIngestBatch(clientBatchId);
    } catch (error: unknown) {
      logger.warn('Cancel: server cancel POST failed (ignored)', error);
      return { clientBatchId, found: false };
    }
  }
);

// ── Workbench: ASN list ────────────────────────────────────────────────────

export const fetchAsnList = createAsyncThunk(
  'packAnalysisWorkbench/fetchAsnList',
  async (
    args: {
      tenantId: number;
      status?: string;
      buyerCodeHint?: string;
      supplierCodeHint?: string;
      // pack-analysis-edi-module-permissions Slice 2b — active buyer/supplier party
      // ids. A party-scoped caller (no global workbench.read) is restricted server-side
      // to the party they are authorized for; ignored for global callers.
      buyerPartyId?: number | null;
      supplierPartyId?: number | null;
      // Pagination-v2 Req 3.1: ISO 8601 UTC strings; half-open per Req 3.6.
      dateFrom?: string | null;
      dateTo?: string | null;
      pageIndex?: number;
      pageSize?: number;
      // Spec pack-analysis-asn-browser-server-sort.
      sortBy?: string | null;
      sortDir?: 'asc' | 'desc' | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const { tenantId, ...params } = args;
      return await packAnalysisService.listAsn(tenantId, params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to list ASNs');
    }
  }
);

// ── Workbench: ASN drill-down ─────────────────────────────────────────────

// Spec pack-analysis-asn-browser-perf-fix Req 4.2 — sentinel error message
// for axios timeout / cancellation. Dialog matches on this prefix to surface
// a localized toast instead of the raw axios error string. Matches the
// 60_000 ms ceiling set by getAsnCanonical().
export const ASN_CANONICAL_TIMEOUT_ERROR_TAG = 'ASN_CANONICAL_TIMEOUT';

export const fetchAsnCanonical = createAsyncThunk(
  'packAnalysisWorkbench/fetchAsnCanonical',
  async (
    args: { tenantId: number; doc856Id: number },
    { rejectWithValue }
  ) => {
    try {
      const data = await packAnalysisService.getAsnCanonical(args.tenantId, args.doc856Id);
      return { doc856Id: args.doc856Id, data };
    } catch (error: unknown) {
      // Spec pack-analysis-asn-browser-perf-fix Req 4.2 — detect axios timeout/cancel
      // and surface a structured tag the UI can match on (instead of bubbling
      // the raw error string verbatim).
      if (axios.isAxiosError(error)
          && (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED')) {
        return rejectWithValue(ASN_CANONICAL_TIMEOUT_ERROR_TAG);
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch ASN drill-down');
    }
  }
);

// ── Workbench: PackMaster list ────────────────────────────────────────────

export const fetchPackMasterList = createAsyncThunk(
  'packAnalysisWorkbench/fetchPackMasterList',
  async (
    args: {
      tenantId: number;
      doc856Id?: number;
      compositionSignature?: string;
      mixKind?: string;
      buyerPartyId?: number;
      supplierPartyId?: number;
      pageIndex?: number;
      pageSize?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { tenantId, ...params } = args;
      return await packAnalysisService.listPackMasters(tenantId, params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to list PackMasters');
    }
  }
);

// ── Workbench: PackMaster single row ──────────────────────────────────────

export const fetchPackMasterById = createAsyncThunk(
  'packAnalysisWorkbench/fetchPackMasterById',
  async (
    args: { tenantId: number; id: number },
    { rejectWithValue }
  ) => {
    try {
      return await packAnalysisService.getPackMaster(args.tenantId, args.id);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch PackMaster');
    }
  }
);

// ── Workbench: Cartonization simulator ────────────────────────────────────

export const cartonizeSimulate = createAsyncThunk(
  'packAnalysisWorkbench/cartonizeSimulate',
  async (payload: CartonizationRequest, { rejectWithValue }) => {
    try {
      return await packAnalysisService.cartonize(payload);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Cartonization simulate failed');
    }
  }
);

/**
 * Pack Planner — PROGRESSIVE mode cartonization (Req 11.1).
 *
 * Drives the Plan card, By Carton / By Item views, §0 Plan Build Timeline,
 * and §2 Rules Applied. Sent with `includeAlternatives: false +
 * includeEvidence: true + includeUtilization: true` so the BE runs the
 * progressive chain (short-circuit at first match) and emits a trace with
 * the actual consumption story.
 *
 * Renamed from `cartonizePackPlannerSimulate` in spec
 * `pack-planner-build-timeline` Phase A.4.1 to make the mode explicit. The
 * companion thunk `cartonizePackPlannerAlternatives` handles §3 PlansConsidered.
 */
export const cartonizePackPlannerProgressive = createAsyncThunk(
  'packAnalysisWorkbench/cartonizePackPlannerProgressive',
  async (payload: CartonizationRequest, { rejectWithValue }) => {
    try {
      return await packAnalysisService.cartonize(payload);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Cartonization progressive failed');
    }
  }
);

/**
 * Pack Planner — ALTERNATIVES mode cartonization (Req 11.1).
 *
 * Drives §3 PlansConsidered ONLY. Sent with `includeAlternatives: true +
 * includeEvidence: false + includeUtilization: false` — the BE runs every
 * strategy independently on the original ctx (no progressive reduction) so
 * §3 can list each strategy's parallel evaluation as alternatives. Trace +
 * utilization not needed (progressive request carries those).
 *
 * Fires in parallel with `cartonizePackPlannerProgressive` from
 * `PackPlannerTab.onRun`. Per Req 11.5/11.6/11.7, the two requests have
 * independent loading / error state so a failure on one doesn't blank the
 * other's UI.
 */
export const cartonizePackPlannerAlternatives = createAsyncThunk(
  'packAnalysisWorkbench/cartonizePackPlannerAlternatives',
  async (payload: CartonizationRequest, { rejectWithValue }) => {
    try {
      return await packAnalysisService.cartonize(payload);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Cartonization alternatives failed');
    }
  }
);

// ── Workbench: Simulator item-aware line entry (spec …item-aware-line-entry) ──

/**
 * Item-identifier combobox typeahead — search the item master by SKU / UPC /
 * EAN / canonical identifier (`q` matches `canonical_identifier` substring with
 * an alt-key exact-match fallback on the BE). `kind` ⇒ `itemType` filter
 * (`ALL` / `SKU` / `PACK`). Small page — this is a typeahead, not a browser.
 * The slice ignores a fulfilled response whose `q` no longer matches the
 * current input (stale-response guard).
 */
export const searchItemsForSimulator = createAsyncThunk<
  Item[],
  { tenantId: number; q: string; kind?: 'ALL' | 'SKU' | 'PACK' },
  { rejectValue: string }
>(
  'packAnalysisWorkbench/searchItemsForSimulator',
  async ({ tenantId, q, kind = 'ALL' }, { rejectWithValue }) => {
    try {
      // Lazy import — `itemMasterService` → `apiClient` → `@/app/store` is a
      // module-init cycle when this file is eager-required before the store
      // (e.g. in unit tests that mock `packAnalysisService` but not this).
      const { itemMasterService } = await import('../../services/api/itemMasterService');
      const res = await itemMasterService.listItems({ tenantId, q, itemType: kind, pageIndex: 1, pageSize: 8 });
      return res.items;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Item search failed');
    }
  }
);

/**
 * Fetch one item-master row and reduce it to a `ResolvedItemRef` (canonical key
 * = strictest-scope `CANONICAL_ITEMKEY` alt-key, else `canonicalIdentifier`).
 * Returns `null` when the item has no determinable canonical key (caller flags
 * the line and excludes it — Req 5.3). The caller (combobox / picker) decides
 * whether to `setLineResolution` an existing line or `addResolvedLine`.
 */
export const fetchAndResolveItem = createAsyncThunk<
  ResolvedItemRef | null,
  { tenantId: number; itemId: number },
  { rejectValue: string }
>(
  'packAnalysisWorkbench/fetchAndResolveItem',
  async ({ tenantId, itemId }, { rejectWithValue }) => {
    try {
      const { itemMasterService } = await import('../../services/api/itemMasterService');
      const item = await itemMasterService.getItem(tenantId, itemId);
      return toResolvedItemRef(item);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve item');
    }
  }
);

/**
 * Expand a picked `PACK` item into a prepack **group**: fetch its recorded
 * composition (`GET /edi/items/{id}/pack-composition`) and, if there is one,
 * dispatch `addPrepackGroup` (a `PrepackGroupMeta` + one read-only `prepackChild`
 * line per component sub-item). When `replaceLineId` is given (the editor line
 * whose combobox / picker chose the PACK item — still holding the typed prepack
 * code), that line is consumed by the new group instead of leaving a stray
 * "manual key — not resolved" line behind. Rejects with `'NO_COMPOSITION'` when
 * the prepack has no historical PackMaster row yet (Req 3.3 — the caller shows a
 * hint and lets the operator add component lines manually).
 */
export const expandPrepack = createAsyncThunk<
  void,
  { tenantId: number; packItemId: number; packDescription?: string | null; replaceLineId?: string },
  { rejectValue: string }
>(
  'packAnalysisWorkbench/expandPrepack',
  async ({ tenantId, packItemId, packDescription, replaceLineId }, { dispatch, rejectWithValue }) => {
    try {
      const { itemMasterService } = await import('../../services/api/itemMasterService');
      const res = await itemMasterService.getPackComposition(tenantId, packItemId);
      const comp = res.data;
      if (!comp || !comp.subItems || comp.subItems.length === 0) {
        return rejectWithValue('NO_COMPOSITION');
      }
      dispatch(
        addPrepackGroup({
          prepackCode: comp.prepackCode,
          packItemId,
          description: packDescription ?? null,
          replaceLineId,
          subItems: comp.subItems.map((s) => ({
            itemKey: s.itemKey,
            itemId: s.itemId ?? null,
            canonicalIdentifier: s.canonicalIdentifier ?? null,
            description: s.description ?? null,
            quantity: Number(s.quantity) || 0,
            uom: s.uom,
          })),
        })
      );
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to expand prepack');
    }
  }
);

/**
 * Pack Planner spec — parallel typeahead thunk so the packPlanner sub-slice
 * owns its own itemSearch state (Req 1.4). Identical service call as
 * `searchItemsForSimulator`; the slice routes pending / fulfilled / rejected
 * events to `state.packPlanner.itemSearch.*` instead of
 * `state.simulator.itemSearch.*`.
 */
export const searchItemsForPackPlanner = createAsyncThunk<
  Item[],
  { tenantId: number; q: string; kind?: 'ALL' | 'SKU' | 'PACK' },
  { rejectValue: string }
>(
  'packAnalysisWorkbench/searchItemsForPackPlanner',
  async ({ tenantId, q, kind = 'ALL' }, { rejectWithValue }) => {
    try {
      const { itemMasterService } = await import('../../services/api/itemMasterService');
      const res = await itemMasterService.listItems({ tenantId, q, itemType: kind, pageIndex: 1, pageSize: 8 });
      return res.items;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Item search failed');
    }
  }
);

/**
 * Pack Planner spec — parallel prepack-expansion thunk. Same composition
 * fetch as `expandPrepack`; dispatches `addPackPlannerPrepackGroup` instead
 * of the simulator-side `addPrepackGroup` so the new group lands in
 * `state.packPlanner.poLines`/`prepackGroups`.
 */
export const expandPrepackForPackPlanner = createAsyncThunk<
  void,
  { tenantId: number; packItemId: number; packDescription?: string | null; replaceLineId?: string },
  { rejectValue: string }
>(
  'packAnalysisWorkbench/expandPrepackForPackPlanner',
  async ({ tenantId, packItemId, packDescription, replaceLineId }, { dispatch, rejectWithValue }) => {
    try {
      const { itemMasterService } = await import('../../services/api/itemMasterService');
      const res = await itemMasterService.getPackComposition(tenantId, packItemId);
      const comp = res.data;
      if (!comp || !comp.subItems || comp.subItems.length === 0) {
        return rejectWithValue('NO_COMPOSITION');
      }
      dispatch(
        addPackPlannerPrepackGroup({
          prepackCode: comp.prepackCode,
          packItemId,
          description: packDescription ?? null,
          replaceLineId,
          subItems: comp.subItems.map((s) => ({
            itemKey: s.itemKey,
            itemId: s.itemId ?? null,
            canonicalIdentifier: s.canonicalIdentifier ?? null,
            description: s.description ?? null,
            quantity: Number(s.quantity) || 0,
            uom: s.uom,
          })),
        })
      );
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to expand prepack');
    }
  }
);
